import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "@/api/errors";
import { statusIntent } from "@/design";
import { toast } from "@/shared/lib/toast";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";

import { useListingsSummary, useRefreshListings } from "../api/use-listings";
import type { MarketplaceRow } from "../lib/group";
import { MARKETPLACE_LABEL_KEY } from "../lib/labels";
import { relativeTime } from "../lib/relative-time";
import type { Connection, MarketplaceId } from "../lib/types";

import { ListingsDrawer } from "./ListingsDrawer";

const STATUS_LABEL_KEY: Record<Connection["status"], string> = {
  active: "status_active",
  not_connected: "status_not_connected",
  disconnected: "status_disconnected",
  expired: "status_expired",
  revoked: "status_revoked",
};

const TH =
  "px-[var(--sp-3)] py-[var(--sp-2)] text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-subtle)]";
const TD = "px-[var(--sp-3)] py-[var(--sp-3)] align-top text-sm";

interface ConnectionsTableProps {
  rows: MarketplaceRow[];
  onConnect: (marketplace: MarketplaceId) => void;
  onDisconnect: (connection: Connection) => void;
  isConnecting: boolean;
  isDisconnecting: boolean;
}

export function ConnectionsTable({
  rows,
  onConnect,
  onDisconnect,
  isConnecting,
  isDisconnecting,
}: ConnectionsTableProps): JSX.Element {
  const { t } = useTranslation("marketplace_connections");
  return (
    <div className="overflow-x-auto rounded-[var(--r-md)] border border-[var(--border-subtle)] bg-[var(--surface-raised)] shadow-[var(--sh-1)]">
      <table className="w-full border-collapse" data-testid="marketplace-connections-table">
        <thead>
          <tr className="border-b border-[var(--border-subtle)]">
            <th className={TH}>{t("col.marketplace")}</th>
            <th className={TH}>{t("col.shop")}</th>
            <th className={TH}>{t("col.status")}</th>
            <th className={TH}>{t("col.listings")}</th>
            <th className={TH}>{t("col.refreshed")}</th>
            <th className={`${TH} text-right`}>{t("col.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <MarketplaceTableRow
              key={row.marketplace}
              row={row}
              onConnect={() => onConnect(row.marketplace)}
              onDisconnect={() => onDisconnect(row.primary)}
              isConnecting={isConnecting}
              isDisconnecting={isDisconnecting}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface RowProps {
  row: MarketplaceRow;
  onConnect: () => void;
  onDisconnect: () => void;
  isConnecting: boolean;
  isDisconnecting: boolean;
}

function MarketplaceTableRow({
  row,
  onConnect,
  onDisconnect,
  isConnecting,
  isDisconnecting,
}: RowProps): JSX.Element {
  const { t, i18n } = useTranslation("marketplace_connections");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { marketplace, primary, previous } = row;
  const {
    status,
    shop_id,
    shop_code,
    shop_name,
    connected_at,
    disconnected_at,
    warning,
    refresh_expires_at,
    last_error,
  } = primary;
  const suffix = `${marketplace}-${shop_id || "new"}`;
  const isConnected = status === "active";
  const isDead = status === "expired" || status === "revoked";
  const expiringSoon = warning === "refresh_token_expiring_soon";
  const intent = expiringSoon ? "warning" : statusIntent("marketplace_connection", status);
  const marketplaceLabel = t(MARKETPLACE_LABEL_KEY[marketplace]);

  const {
    data: summary,
    isError: isSummaryError,
    error: summaryError,
  } = useListingsSummary(marketplace, isConnected);
  const refreshListings = useRefreshListings(marketplace);

  function handleRefresh(): void {
    refreshListings.mutate(undefined, {
      onSuccess: (result) =>
        toast.success(
          t("listings.toast_refresh_success", {
            mapped: result.mapped,
            unmapped: result.unmapped,
            duplicate: result.duplicate,
          }),
        ),
      onError: (error) => toast.error(apiErrorMessage(error)),
    });
  }

  const deadReason = isDead
    ? t(status === "revoked" ? "reason_revoked" : "reason_expired", {
        marketplace: marketplaceLabel,
        date: disconnected_at ? new Date(disconnected_at).toLocaleString() : "",
      })
    : "";

  return (
    <tr
      className="border-b border-[var(--border-subtle)] last:border-b-0"
      data-testid={`marketplace-row-${suffix}`}
    >
      <td className={`${TD} font-bold text-[var(--text-primary)]`}>{marketplaceLabel}</td>

      <td className={`${TD} text-[var(--text-secondary)]`}>
        {shop_id ? (
          <div className="space-y-0.5">
            <p className="font-medium text-[var(--text-primary)]">
              {shop_name || t("lbl_shop_unnamed")}
            </p>
            <p>
              {shop_code
                ? t("lbl_shop_code", { code: shop_code })
                : t("lbl_shop_id", { id: shop_id })}
              {connected_at && (
                <>
                  {" · "}
                  {t("lbl_connected_at", { date: new Date(connected_at).toLocaleDateString() })}
                </>
              )}
            </p>
            {!isConnected && disconnected_at && (
              <p>
                {t("lbl_disconnected_at", { date: new Date(disconnected_at).toLocaleDateString() })}
              </p>
            )}
            {expiringSoon && refresh_expires_at && (
              <p className="font-medium text-[var(--intent-warning-fg)]" role="alert">
                {t("warn_refresh_expiring", {
                  date: new Date(refresh_expires_at).toLocaleDateString(),
                })}
              </p>
            )}
            {isConnected && last_error && (
              <p
                className="font-medium text-[var(--intent-warning-fg)]"
                role="alert"
                data-testid={`marketplace-attention-${suffix}`}
              >
                {t("warn_attention", { detail: last_error })}
              </p>
            )}
            {isDead && (
              <>
                <p
                  className="font-medium text-[var(--intent-danger-fg)]"
                  role="alert"
                  data-testid={`marketplace-sync-paused-${suffix}`}
                >
                  {t("sync_paused")}
                </p>
                <p data-testid={`marketplace-reason-${suffix}`}>{deadReason}</p>
                {last_error && (
                  <p
                    className="text-[var(--text-subtle)]"
                    data-testid={`marketplace-last-error-${suffix}`}
                  >
                    {t("lbl_last_error", { detail: last_error })}
                  </p>
                )}
              </>
            )}
            {previous.length > 0 && (
              <p
                className="text-[var(--text-subtle)]"
                data-testid={`marketplace-previous-${marketplace}`}
              >
                {t("lbl_previous", { count: previous.length })}
              </p>
            )}
          </div>
        ) : (
          <span className="text-[var(--text-subtle)]">—</span>
        )}
      </td>

      <td className={TD}>
        <Badge intent={intent} data-testid={`marketplace-status-${suffix}`}>
          {expiringSoon ? t("status_expiring_soon") : t(STATUS_LABEL_KEY[status])}
        </Badge>
      </td>

      <td className={`${TD} text-[var(--text-secondary)]`}>
        {isConnected ? (
          <div className="space-y-0.5" data-testid={`marketplace-listings-${suffix}`}>
            {isSummaryError ? (
              <p
                className="font-medium text-[var(--intent-danger-fg)]"
                role="alert"
                data-testid={`marketplace-listings-error-${suffix}`}
              >
                {t("listings.error", { detail: apiErrorMessage(summaryError) })}
              </p>
            ) : (
              <p className="tabular-nums">
                {t("listings.line", {
                  mapped: summary?.mapped ?? 0,
                  unmapped: summary?.unmapped ?? 0,
                  duplicate: summary?.duplicate ?? 0,
                })}
              </p>
            )}
            {!isSummaryError && (
              // Screen-reader copy of the "Last refresh" column, kept in this
              // cell so the listings line reads as one unit.
              <p className="sr-only">
                {summary?.ran_at
                  ? t("listings.refreshed", { rel: relativeTime(summary.ran_at, i18n.language) })
                  : t("listings.never")}
              </p>
            )}
            {summary?.multi_warehouse && (
              <p
                className="font-medium text-[var(--intent-warning-fg)]"
                role="alert"
                data-testid={`marketplace-multi-warehouse-${suffix}`}
              >
                ⛔ {t("listings.multi_wh", { n: summary.warehouse_count })}
              </p>
            )}
          </div>
        ) : (
          <span className="text-[var(--text-subtle)]">—</span>
        )}
      </td>

      <td className={`${TD} whitespace-nowrap text-[var(--text-secondary)]`}>
        {isConnected && !isSummaryError
          ? summary?.ran_at
            ? relativeTime(summary.ran_at, i18n.language)
            : t("listings.never")
          : "—"}
      </td>

      <td className={`${TD} whitespace-nowrap text-right`}>
        {isConnected ? (
          <div className="flex items-center justify-end gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDrawerOpen(true)}
              data-testid={`listings-view-btn-${suffix}`}
            >
              {t("listings.view")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={refreshListings.isPending}
              onClick={handleRefresh}
              data-testid={`listings-refresh-btn-${suffix}`}
            >
              {t("listings.refresh")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={isConnecting}
              onClick={onConnect}
              data-testid={`switch-btn-${suffix}`}
            >
              {t("btn_switch")}
            </Button>
            <span className="mx-1 text-[var(--border-subtle)]">·</span>
            <Button
              variant="ghost"
              size="sm"
              disabled={isDisconnecting}
              onClick={onDisconnect}
              data-testid={`disconnect-btn-${suffix}`}
            >
              {t("btn_disconnect")}
            </Button>
          </div>
        ) : (
          <Button
            variant="default"
            size="sm"
            disabled={isConnecting}
            onClick={onConnect}
            data-testid={`connect-btn-${suffix}`}
          >
            {shop_id ? t("btn_reconnect") : t("btn_connect")}
          </Button>
        )}
        {isConnected && (
          <ListingsDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            mkp={marketplace}
          />
        )}
      </td>
    </tr>
  );
}
